/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Calepinage\ImageModal.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Modal pour afficher les images de calepinage en plein écran
 * Permet la visualisation et le téléchargement d'images HD
 */

import React from 'react';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageBase64: string | null;
  groupeId: string;
  matiereNom?: string;
  couleur?: string;
  isLoading?: boolean;
}

const ImageModal: React.FC<ImageModalProps> = ({ 
  isOpen, 
  onClose, 
  imageBase64, 
  groupeId, 
  matiereNom,
  couleur,
  isLoading = false 
}) => {
  if (!isOpen) return null;

  const downloadImage = () => {
    if (!imageBase64) return;
    
    // Utiliser le nom réel de la matière si disponible, sinon fallback sur l'ID
    let nomFichier;
    if (matiereNom && couleur) {
      nomFichier = `Calepinage_hd_${matiereNom}_${couleur}.png`;
    } else {
      // Fallback : extraire depuis groupeId (format: "1-Blanc")
      const [matiereId, couleurFallback] = groupeId.split('-');
      nomFichier = `Calepinage_hd_matiere${matiereId}_${couleurFallback}.png`;
    }
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageBase64}`;
    link.download = nomFichier;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="max-w-[95vw] max-h-[95vh] flex flex-col">
        {/* Header avec boutons */}
        <div className="flex justify-between items-center mb-4 px-4">
          <h3 className="text-white text-lg font-medium">
            Calepinage - {groupeId}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={downloadImage}
              disabled={!imageBase64 || isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Télécharger PNG
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Fermer
            </button>
          </div>
        </div>

        {/* Contenu de l'image */}
        <div className="flex-1 flex items-center justify-center bg-white rounded-lg p-4">
          {isLoading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-600">Génération de l'image HD...</p>
            </div>
          ) : imageBase64 ? (
            <img 
              src={`data:image/png;base64,${imageBase64}`}
              alt={`Calepinage ${groupeId}`}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: 'calc(95vh - 120px)' }}
            />
          ) : (
            <div className="text-center">
              <p className="text-gray-500 mb-2">Erreur lors du chargement de l'image</p>
              <button
                onClick={onClose}
                className="text-blue-600 hover:text-blue-800"
              >
                Fermer
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="text-center mt-4">
          <p className="text-white text-sm opacity-75">
            Cliquez en dehors de l'image ou sur "Fermer" pour quitter
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
